import { DateTime } from 'luxon';
import { IntlShape } from 'react-intl';

import { DeviceStatus, FulfillmentStatus } from 'constants/enums';

const SMART_METER_AWAITING_FULFILLMENT = 'awaiting_fulfillment';

const DAY_FORMAT: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };

/** The device's own provider track, or `undefined` when that provider has reported nothing. */
export const getFulfillmentTrack = (device: IDeviceListItem): IFulfillmentTrack | undefined => {
  const provider = (device.deviceOrdering?.shippingProvider ?? '').toLowerCase();
  if (!provider) return undefined;
  return (device.purchaseOrder?.fulfillment ?? []).find(
    (track) => (track.provider ?? '').toLowerCase() === provider
  );
};

/** `Oct 7` in the viewer's zone: a fulfillment event is an instant. */
export const formatFulfillmentDay = (
  iso: string | null | undefined,
  locale: string
): string | null => {
  if (!iso) return null;
  const date = DateTime.fromISO(iso).setLocale(locale);
  return date.isValid ? date.toLocaleString(DAY_FORMAT) : null;
};

/** `Oct 9` read in UTC, like a promised delivery day, so every viewer sees the same day. */
export const formatEstimatedShipDay = (
  iso: string | null | undefined,
  locale: string
): string | null => {
  if (!iso) return null;
  const date = DateTime.fromISO(iso, { zone: 'utc' }).setLocale(locale);
  return date.isValid ? date.toLocaleString(DAY_FORMAT) : null;
};

/** Step name for a normalised status; an unknown status falls back to the provider's raw value. */
export const getFulfillmentStepLabel = (
  formatMessage: IntlShape['formatMessage'],
  status: string,
  providerStatus?: string | null
): string => {
  switch (status) {
    case FulfillmentStatus.RECEIVED:
      return formatMessage({ id: 'devices.fulfillment.status.received' });
    case FulfillmentStatus.PLANNING:
      return formatMessage({
        id:
          (providerStatus ?? '').toLowerCase() === SMART_METER_AWAITING_FULFILLMENT
            ? 'devices.fulfillment.status.awaitingFulfillment'
            : 'devices.fulfillment.status.planning'
      });
    case FulfillmentStatus.PROCESSING:
      return formatMessage({ id: 'devices.fulfillment.status.processing' });
    case FulfillmentStatus.SHIPPED:
      return formatMessage({ id: 'shippedKey' });
    case FulfillmentStatus.DELIVERED:
      return formatMessage({ id: 'deliveredKey' });
    case FulfillmentStatus.CANCELLED:
      return formatMessage({ id: 'cancelledKey' });
    case FulfillmentStatus.EXCEPTION:
      return formatMessage({ id: 'devices.fulfillment.status.exception' });
    default:
      return providerStatus || status;
  }
};

/** Like the step label, with the provider's reason on an exception: `Exception (Unfulfillable)`. */
export const getFulfillmentStepDetailLabel = (
  formatMessage: IntlShape['formatMessage'],
  status: string,
  providerStatus?: string | null
): string => {
  const label = getFulfillmentStepLabel(formatMessage, status, providerStatus);
  const showReason =
    status === FulfillmentStatus.EXCEPTION &&
    !!providerStatus &&
    providerStatus.toLowerCase() !== FulfillmentStatus.EXCEPTION;
  return showReason ? `${label} (${providerStatus})` : label;
};

export interface IFulfillmentStep {
  status: string;
  providerStatus: string | null;
  occurredAt: string | null;
  reached: boolean;
}

const AFTER_SHIPPING = new Set<string>([FulfillmentStatus.SHIPPED, FulfillmentStatus.DELIVERED]);

const toMillis = (iso: string) => DateTime.fromISO(iso).toMillis();

const byOccurredAt = (a: IFulfillmentEvent, b: IFulfillmentEvent) =>
  toMillis(a.occurredAt) - toMillis(b.occurredAt);

const toStep = (event: IFulfillmentEvent): IFulfillmentStep => ({
  status: event.status,
  providerStatus: event.providerStatus,
  occurredAt: event.occurredAt,
  reached: true
});

/**
 * The provider's latest step before shipping. A split order's track also carries its other
 * shipments' Shipped and Delivered, which say nothing about a device that has not left yet.
 */
export const getLatestPreShipmentEvent = (
  track: IFulfillmentTrack | undefined
): IFulfillmentEvent | undefined =>
  (track?.events ?? [])
    .filter((event) => !AFTER_SHIPPING.has(event.status))
    .sort(byOccurredAt)
    .pop();

/**
 * The stepper for one device, or `null` when its provider has reported nothing for it.
 *
 * The track is the whole order's, so only this device's part of it counts: steps before shipping
 * up to the moment the device shipped, the Shipped event of its own shipment (the latest one by
 * the time it shipped), and Delivered only once the device itself is delivered. Shipped and
 * Delivered also light up from the device's own status when no event backs them.
 *
 * A status reached more than once sits where it last happened, so an exception that resolved back
 * to processing ends on processing. Not-yet-reached Shipped and Delivered are dropped once the
 * order is cancelled.
 */
export const buildFulfillmentSteps = (
  device: IDeviceListItem,
  track: IFulfillmentTrack | undefined
): IFulfillmentStep[] | null => {
  const deviceShipped =
    device.status === DeviceStatus.SHIPPED || device.status === DeviceStatus.DELIVERED;
  const shippedAtIso = deviceShipped ? device.shippingRecord?.shippedAt : undefined;
  const shippedAt = shippedAtIso ? toMillis(shippedAtIso) : undefined;
  const events = (track?.events ?? [])
    .filter((event) => {
      switch (event.status) {
        case FulfillmentStatus.SHIPPED:
          return shippedAt !== undefined && toMillis(event.occurredAt) <= shippedAt;
        case FulfillmentStatus.DELIVERED:
          return device.status === DeviceStatus.DELIVERED;
        default:
          return shippedAt === undefined || toMillis(event.occurredAt) <= shippedAt;
      }
    })
    .sort(byOccurredAt);
  if (!events.length) return null;

  const latest = new Map<string, IFulfillmentEvent>();
  events.forEach((event) => {
    latest.delete(event.status);
    latest.set(event.status, event);
  });

  const received = latest.get(FulfillmentStatus.RECEIVED);
  latest.delete(FulfillmentStatus.RECEIVED);
  const reached = Array.from(latest.values()).map(toStep);
  if (deviceShipped) {
    // Stable sort: pre-shipment steps keep their relative order, ahead of Shipped and Delivered.
    reached.sort(
      (a, b) => Number(AFTER_SHIPPING.has(a.status)) - Number(AFTER_SHIPPING.has(b.status))
    );
  }

  if (deviceShipped && !latest.has(FulfillmentStatus.SHIPPED)) {
    const shipped: IFulfillmentStep = {
      status: FulfillmentStatus.SHIPPED,
      providerStatus: null,
      occurredAt: device.shippingRecord?.shippedAt ?? null,
      reached: true
    };
    const deliveredIndex = reached.findIndex((s) => s.status === FulfillmentStatus.DELIVERED);
    if (deliveredIndex >= 0) reached.splice(deliveredIndex, 0, shipped);
    else reached.push(shipped);
  }
  if (device.status === DeviceStatus.DELIVERED && !latest.has(FulfillmentStatus.DELIVERED)) {
    reached.push({
      status: FulfillmentStatus.DELIVERED,
      providerStatus: null,
      occurredAt: null,
      reached: true
    });
  }

  const steps: IFulfillmentStep[] = [
    received
      ? toStep(received)
      : {
          status: FulfillmentStatus.RECEIVED,
          providerStatus: null,
          occurredAt: null,
          reached: reached.length > 0
        },
    ...reached
  ];

  const last = steps[steps.length - 1];
  if (last.status !== FulfillmentStatus.CANCELLED) {
    [FulfillmentStatus.SHIPPED, FulfillmentStatus.DELIVERED].forEach((status) => {
      if (!steps.some((s) => s.status === status)) {
        steps.push({ status, providerStatus: null, occurredAt: null, reached: false });
      }
    });
  }
  return steps;
};
